/**
 * Manages the desktop header sub-menu drawers.
 *
 * Behaviour:
 * - Top-level nav links with .menu-item-has-children open their associated
 *   .sub-menu panel (id = aria-controls value).
 * - Only one panel is visible at a time. Switching between triggers does NOT
 *   close+reopen the drawer; instead the backdrop (.header__menu-bg) animates
 *   its height to match the newly active panel's natural height, and panels
 *   cross-fade beneath it.
 * - A single global close button (.header__close) closes whichever panel is
 *   open. Clicking the backdrop or pressing Escape also closes.
 */

export function initializeInteractiveHeader() {
    const header = document.getElementById('header');
    if (!header) return;

    const menuBg = header.querySelector('.header__menu-bg');
    const closeBtn = header.querySelector('.header__close');
    const primaryTriggers = header.querySelectorAll('.header__primary > ul > li.menu-item-has-children > a');
    const navTriggers = [...primaryTriggers];

    const subMenus = [];
    navTriggers.forEach(trigger => {
        const panelId = trigger.getAttribute('aria-controls');
        if (panelId) {
            const panel = document.getElementById(panelId);
            if (panel) subMenus.push(panel);
        }
    });

    let activeTrigger = null;
    let activePanel = null;

    /**
     * Measure a panel's natural height. Panels are display: none when hidden,
     * so temporarily reveal them off-screen to read the size.
     *
     * @param {HTMLElement} panel
     * @returns {number}
     */
    const measurePanel = (panel) => {
        const prev = {
            visibility: panel.style.visibility,
            position: panel.style.position,
            display: panel.style.display,
        };
        panel.style.visibility = 'hidden';
        panel.style.position = 'absolute';
        panel.style.display = 'block';
        const h = panel.scrollHeight;
        panel.style.visibility = prev.visibility;
        panel.style.position = prev.position;
        panel.style.display = prev.display;
        return h;
    };

    const setBgHeight = (px) => {
        if (!menuBg) return;
        menuBg.style.height = px + 'px';
    };

    const closeAllPanels = () => {
        subMenus.forEach(panel => {
            panel.classList.remove('is-visible');
            panel.setAttribute('aria-hidden', 'true');
        });
        navTriggers.forEach(trigger => {
            trigger.classList.remove('is-active');
            trigger.setAttribute('aria-expanded', 'false');
        });
        document.body.classList.remove('menu-open');
        if (closeBtn) closeBtn.hidden = true;
        if (menuBg) {
            menuBg.classList.remove('is-visible');
            menuBg.style.height = '';
        }
        if (activeTrigger && document.body.contains(activeTrigger)) {
            activeTrigger.focus();
        }
        activeTrigger = null;
        activePanel = null;
    };

    const openSubMenu = (trigger) => {
        const panelId = trigger.getAttribute('aria-controls');
        const panel = document.getElementById(panelId);
        if (!panel) return;

        // Toggle off if same panel is clicked again.
        if (panel === activePanel) {
            closeAllPanels();
            return;
        }

        // Hide whichever was previously active without resetting the backdrop.
        subMenus.forEach(p => {
            if (p !== panel) {
                p.classList.remove('is-visible');
                p.setAttribute('aria-hidden', 'true');
            }
        });
        navTriggers.forEach(t => {
            if (t !== trigger) {
                t.classList.remove('is-active');
                t.setAttribute('aria-expanded', 'false');
            }
        });

        // Reveal the new one and morph the backdrop height to match.
        const targetHeight = measurePanel(panel);
        panel.classList.add('is-visible');
        panel.setAttribute('aria-hidden', 'false');
        trigger.classList.add('is-active');
        trigger.setAttribute('aria-expanded', 'true');

        document.body.classList.add('menu-open');
        if (closeBtn) closeBtn.hidden = false;
        if (menuBg) {
            menuBg.classList.add('is-visible');
            setBgHeight(targetHeight);
        }

        activeTrigger = trigger;
        activePanel = panel;
    };

    // Desktop only — mobile-menu.js handles its own accordion behaviour on
    // these same triggers. The breakpoint mirrors scaff01d's md threshold.
    const isDesktop = () => window.innerWidth > 1080;

    primaryTriggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
            if (!isDesktop()) return;
            e.preventDefault();
            openSubMenu(trigger);
        });
    });

    if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            closeAllPanels();
        });
    }

    if (menuBg) {
        menuBg.addEventListener('click', closeAllPanels);
    }

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && activePanel) {
            closeAllPanels();
        }
    });

    document.addEventListener('barba-leave-start', closeAllPanels);

    header.querySelectorAll('.header .sub-menu a').forEach(link => {
        link.addEventListener('click', closeAllPanels);
    });

    // Re-measure the active panel on resize so the backdrop tracks reflow.
    window.addEventListener('resize', () => {
        if (activePanel) {
            setBgHeight(measurePanel(activePanel));
        }
    });
}
